import type * as api from '@gitbook/api-client';
import type { Block, Document, Inline } from '@gitbook/slate';
import { assertNever } from '@gitbook/util-ts';

import { getTableRecordValue } from './table';
import {
    BLOCK_CONTENT_REF,
    BLOCK_DRAWING,
    BLOCK_FILE,
    BLOCK_IMAGE,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_TABLE,
    type BlockContentRefData,
    type BlockDrawingData,
    type BlockFileData,
    type BlockImageData,
    type BlockOpenAPIData,
    type BlockOpenAPIOperationData,
    type BlockOpenAPISchemasData,
    type BlockReusableContentData,
    type BlockTableData,
    INLINE_BUTTON,
    INLINE_IMAGE,
    INLINE_LINK,
    INLINE_MENTION,
    type InlineButtonData,
    type InlineImageData,
    type InlineLinkData,
    type InlineMentionData,
    type TableRecordValue,
} from './types';

/**
 * A map of content references by node key.
 */
type ContentReferenceMap = { [nodeKey: string]: api.ContentRef[] };

/**
 * Extract content references from a node.
 */
export function extractContentRefs(
    document: Document,
    options?: {
        filter?: (ref: api.ContentRef) => boolean;
    }
): ContentReferenceMap {
    const result: ContentReferenceMap = {};

    mapContentRefs(document, (node, ref) => {
        const shouldInclude = options?.filter?.(ref) ?? true;
        if (shouldInclude) {
            result[node.key] = (result[node.key] ?? []).concat(ref);
        }
        return ref;
    });

    return result;
}

/**
 * Extract the outbound link URLs of a document: the URL-kind refs of link-like
 * nodes (inline links, buttons, content-ref blocks).
 */
export function extractOutboundLinkUrls(document: Document): string[] {
    const urls = new Set<string>();

    mapContentRefs(document, (node, ref) => {
        if (
            ref.kind === 'url' &&
            (node.type === INLINE_LINK ||
                node.type === INLINE_BUTTON ||
                node.type === BLOCK_CONTENT_REF)
        ) {
            urls.add(ref.url);
        }
        return ref;
    });

    return Array.from(urls);
}

/**
 * Map all content-refs in a document to new value.
 */
export function mapContentRefs(
    document: Document,
    mapContentRef: (node: Block | Inline, ref: api.ContentRef) => api.ContentRef
): Document {
    return document.mapDescendants((node) => {
        if (node.object === 'block') {
            switch (node.type) {
                case BLOCK_OPENAPI:
                case BLOCK_OPENAPI_OPERATION:
                case BLOCK_OPENAPI_SCHEMAS:
                case BLOCK_OPENAPI_WEBHOOK:
                case BLOCK_REUSABLE_CONTENT:
                case BLOCK_CONTENT_REF:
                case BLOCK_FILE:
                case BLOCK_DRAWING: {
                    const data = node.data.toObject<
                        | BlockContentRefData
                        | BlockOpenAPIOperationData
                        | BlockOpenAPIData
                        | BlockOpenAPISchemasData
                        | BlockReusableContentData
                        | BlockDrawingData
                        | BlockFileData
                    >();

                    if (data.ref) {
                        const ref = mapContentRef(node, data.ref);
                        return ref === data.ref ? node : node.setData(['ref'], ref);
                    }

                    return node;
                }
                case BLOCK_IMAGE: {
                    const data = node.data.toObject<BlockImageData>();
                    const ref = mapContentRef(node, data.ref);
                    const refDark = data.refDark ? mapContentRef(node, data.refDark) : undefined;
                    return ref === data.ref && refDark === data.refDark
                        ? node
                        : node.setData(['ref'], ref).setData(['refDark'], refDark);
                }
                case BLOCK_TABLE: {
                    const initialData = node.data.toObject<BlockTableData>();
                    let data = initialData;

                    const updateRecord = (
                        recordId: string,
                        columnId: string,
                        value: TableRecordValue
                    ) => {
                        if (data === initialData) {
                            data = structuredClone(data);
                        }
                        data.records[recordId].values[columnId] = value;
                    };

                    Object.values(data.definition).forEach((columnDefinition) => {
                        if (columnDefinition.type === 'content-ref') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const ref = getTableRecordValue(record, columnDefinition);

                                if (ref) {
                                    const mappedRef = mapContentRef(node, ref);
                                    if (mappedRef !== ref) {
                                        updateRecord(recordId, columnId, mappedRef);
                                    }
                                }
                            });
                        } else if (columnDefinition.type === 'files') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const fileIds = getTableRecordValue(record, columnDefinition);

                                if (Array.isArray(fileIds)) {
                                    const newFileIds: string[] = [];
                                    let hasChanged = false;

                                    fileIds.forEach((fileId) => {
                                        const mappedRef = mapContentRef(node, {
                                            kind: 'file',
                                            file: fileId,
                                        });

                                        if (mappedRef.kind !== 'file') {
                                            throw new Error(
                                                `Expected file reference, got ${mappedRef.kind} for fileId ${fileId}`
                                            );
                                        }

                                        if (mappedRef.file !== fileId) {
                                            hasChanged = true;
                                        }

                                        newFileIds.push(mappedRef.file);
                                    });

                                    if (hasChanged) {
                                        updateRecord(recordId, columnId, newFileIds);
                                    }
                                }
                            });
                        } else if (columnDefinition.type === 'users') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const userIds = getTableRecordValue(record, columnDefinition);

                                if (Array.isArray(userIds)) {
                                    const newUserIds: string[] = [];
                                    let hasChanged = false;

                                    userIds.forEach((userId) => {
                                        const mappedRef = mapContentRef(node, {
                                            kind: 'user',
                                            user: userId,
                                        });

                                        if (mappedRef.kind !== 'user') {
                                            throw new Error(
                                                `Expected user reference, got ${mappedRef.kind} for userId ${userId}`
                                            );
                                        }

                                        if (mappedRef.user !== userId) {
                                            hasChanged = true;
                                        }

                                        newUserIds.push(mappedRef.user);
                                    });

                                    if (hasChanged) {
                                        updateRecord(recordId, columnId, newUserIds);
                                    }
                                }
                            });
                        } else if (columnDefinition.type === 'image') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const value = getTableRecordValue(record, columnDefinition);

                                if (!value) {
                                    return;
                                }

                                const ref = 'ref' in value ? value.ref : value;
                                if (!ref) {
                                    return;
                                }

                                const mappedRef = mapContentRef(node, ref);
                                if (mappedRef !== ref) {
                                    if (mappedRef.kind !== 'file' && mappedRef.kind !== 'url') {
                                        throw new Error(
                                            `Expected file or url reference, got ${mappedRef.kind} for image column`
                                        );
                                    }

                                    const mappedValue: api.DocumentTableImageRecord = {
                                        objectFit: 'ref' in value ? value.objectFit : undefined,
                                        ref: mappedRef,
                                        alt: 'alt' in value ? value.alt : undefined,
                                    };

                                    updateRecord(recordId, columnId, mappedValue);
                                }
                            });
                        }
                    });

                    return data !== initialData ? node.setData(['records'], data.records) : node;
                }
            }
        }

        if (node.object === 'inline') {
            switch (node.type) {
                case INLINE_LINK:
                case INLINE_MENTION:
                case INLINE_IMAGE: {
                    const data = node.data.toObject<
                        InlineImageData | InlineMentionData | InlineLinkData
                    >();
                    const ref = mapContentRef(node, data.ref);
                    return ref === data.ref ? node : node.setData(['ref'], ref);
                }
                case INLINE_BUTTON: {
                    const data = node.data.toObject<InlineButtonData>();
                    if ('ref' in data) {
                        const ref = mapContentRef(node, data.ref);
                        return ref === data.ref ? node : node.setData(['ref'], ref);
                    }
                    return node;
                }
            }
        }

        return node;
    });
}

/**
 * Map all content-refs in a document to absolute values using a given space.
 */
export function mapContentRefsToAbsolute(
    document: Document,
    { spaceId, pageId }: { spaceId: string; pageId: string | undefined }
) {
    return mapContentRefs(document, (_node, ref) => {
        switch (ref.kind) {
            case 'page':
                return {
                    ...ref,
                    space: ref.space ?? spaceId,
                };
            case 'file':
                return {
                    ...ref,
                    space: ref.space ?? spaceId,
                };
            case 'anchor':
                return {
                    ...ref,
                    space: ref.space ?? spaceId,
                    page: ref.page ?? pageId,
                };
            case 'reusable-content':
                return {
                    ...ref,
                    space: ref.space ?? spaceId,
                };
            case 'tag':
                return {
                    ...ref,
                    space: ref.space ?? spaceId,
                };
            case 'url':
            case 'user':
            case 'space':
            case 'collection':
            case 'openapi':
                return ref;
            default:
                assertNever(ref);
        }
    });
}
